import { z } from 'zod'

import { parseCursorPage } from '@/shared/api/paging'
import { auditShape } from '@/shared/details'

import { coursePath } from '../courses/paths'

export const courseFileKinds = ['file', 'link'] as const

export type CourseFileKind = (typeof courseFileKinds)[number]

const fileSchema = z
  .object({
    id: z.string(),
    courseId: z.string(),
    kind: z.enum(courseFileKinds),
    name: z.string(),
    description: z.string(),
    isVisibleToStudents: z.boolean(),
    isVisibleToInstructors: z.boolean(),
    url: z.string().nullish(),
    fileName: z.string().nullish(),
    fileSize: z.number().nullish(),
  })
  .extend(auditShape)

/** One of a course's resources: a stored file or a link. */
export type CourseFile = z.infer<typeof fileSchema>

export function parseCourseFile(body: unknown): CourseFile {
  return fileSchema.parse(body)
}

/** A course's files and links, by name. */
export function parseCourseFiles(body: unknown): readonly CourseFile[] {
  return parseCursorPage(body, parseCourseFile).data
}

const contentSchema = z.object({ url: z.string().min(1) })

/** The link that opens a file now. */
export function parseCourseFileContent(body: unknown): string {
  return contentSchema.parse(body).url
}

/** The API's course file endpoints. */
export function courseFilesApi(courseId: string): string {
  return `/catalog/v1/courses/${encodeURIComponent(courseId)}/files`
}

export function courseFileApi(courseId: string, fileId: string): string {
  return `${courseFilesApi(courseId)}/${encodeURIComponent(fileId)}`
}

/** Manage Resources, and its pages to add or edit one. */
export const manageResourcesRoute = '/catalog/courses/:publicId/resources'
export const createResourceRoute = `${manageResourcesRoute}/new`
export const editResourceRoute = `${manageResourcesRoute}/:fileId/edit`

export function manageResourcesPath(courseId: string): string {
  return `${coursePath(courseId)}/resources`
}

export function createResourcePath(courseId: string): string {
  return `${manageResourcesPath(courseId)}/new`
}

export function editResourcePath(courseId: string, fileId: string): string {
  return `${manageResourcesPath(courseId)}/${encodeURIComponent(fileId)}/edit`
}

/** Opening a course's files without a class or course assignment. */
export const readCourseFilesPermission = 'catalog.courses.files.read'
